import { computed, ref } from 'vue'
import { freezeObject, isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'

const TABLE_KEY = 'products'
const UI_VIEW = 'temporary_regulations_products'
const MINIMUM_MARKERS_TO_SHOW_CHECKBOXES = 1

export function useTable (options) {
  const {
    trans,
    router,
    onMapLoadFn,
    fetchMarkersFn,
    productsMapComponent,
    showMarkersFn,
    totalMarkersCount,
    activeIconComponent,
    openInfoDialogFn,
    closeInfoDialogFn
  } = options || {}

  const isLoading = ref(false)
  const isCreating = ref(false)
  // Only worth showing checkboxes (the way to reveal a route's markers) once
  // there's more than one marker to actually pick between.
  const showTableCheckboxes = computed(() => (totalMarkersCount?.value || 0) > MINIMUM_MARKERS_TO_SHOW_CHECKBOXES)

  const setLoader = (value) => {
    isLoading.value = value
  }

  const apiConfig = freezeObject({
    get: {
      module: 'road-maintenance',
      route: 'products'
    }
  })

  const breadcrumbs = computed(() => {
    const baseItems = [
      {
        text: trans('road-maintenance.settings')
      },
      {
        text: trans('road-maintenance/work_orders.products'),
        bolded: true
      }
    ]

    if (isCreating.value) {
      baseItems[1].bolded = false
      baseItems.push({
        text: trans('road-maintenance/inventory.adding_new_item'),
        bolded: true
      })
    }

    return {
      items: baseItems
    }
  })

  const paginationConfig = freezeObject({
    perPageOptions: [50, 100],
    perPage: 50,
    currentPage: 1
  })

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'id',
        label: trans('road-maintenance/settings.id')
      },
      {
        key: 'barcode',
        label: trans('road-maintenance/inventory.barcode')
      },
      {
        key: 'description',
        label: trans('road-maintenance/inventory.item_description')
      },
      {
        key: 'typeId',
        label: trans('road-maintenance/inventory.item_category'),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'product-types/autocomplete',
          custom_display_value: (event) => {
            return event?.description || ''
          }
        }
      }
    ]
  }))

  const refreshData = () => {
    const { fetchData } = registeredTableHandlers?.[TABLE_KEY] || {}
    if (isFunction(fetchData)) {
      fetchData()
    }

    // The markers go with the listing. Their count is what decides whether the rows
    // offer checkboxes at all, and it is only ever set by this fetch - without it a
    // product added, deleted or deactivated left both the map and the checkboxes as
    // they were until the page was reloaded.
    if (isFunction(fetchMarkersFn)) {
      fetchMarkersFn()
    }
  }

  const deleteProductHandler = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`products/${id}`)
      closeInfoDialogFn()
      refreshData()
    }
    catch (exception) {
      console.error(exception)
    }
  }

  const onProductDelete = (event) => {
    openInfoDialogFn({
      title: trans('road-maintenance/inventory/product.delete_title') + (event?.data?.description ? ' - ' + event.data.description : ''),
      message: trans('road-maintenance/inventory/product.delete_message'),
      onConfirm: () => deleteProductHandler(event)
    })
  }

  const onShowProductDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: 'TemporaryRegulationsProductDetails',
      params: {
        id
      }
    })
  }

  const onProductActivation = async (event) => {
    const { deactivated_at: deactivatedAt, description } = event?.data || {}
    const descriptionTitle = description?.length > 25
      ? description.substring(0, 25) + '...'
      : description || ''

    const activationMessage = deactivatedAt ? 'activation' : 'deactivation'

    openInfoDialogFn({
      title: trans(`road-maintenance/work_orders.product_${activationMessage}`) + (descriptionTitle ? ' - ' + descriptionTitle : ''),
      message: trans(`road-maintenance/work_orders.product_${activationMessage}_message`),
      onConfirm: () => productActivationHandler(event)
    })
  }

  const productActivationHandler = async (event) => {
    try {
      const { id, deactivated_at: deactivatedAt } = event?.data || {}
      if (!id) {
        return
      }

      await api('road-maintenance').patch(`products/${id}/${deactivatedAt ? 'activate' : 'deactivate'}`)
      closeInfoDialogFn()
      refreshData()
    }
    catch (exception) {
      console.error(exception)
    }
  }

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowProductDetails),
    {
      label: (event) => {
        const { deactivated_at: deactivatedAt } = event?.data || {}
        return deactivatedAt ? trans('base.activate') : trans('base.deactivate')
      },
      icon: (event) => {
        const { deactivated_at: deactivatedAt } = event?.data || {}
        return deactivatedAt ? 'mdi-check-circle' : 'mdi-close-circle'
      },
      click: onProductActivation
    },
    deleteAction(trans, onProductDelete)
  ]))

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/settings.id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans('road-maintenance/inventory.barcode'),
      key: 'barcode',
      always_visible: true
    },
    {
      label: trans('road-maintenance/inventory.item_description'),
      key: 'description',
      always_visible: true
    },
    {
      label: trans('road-maintenance/inventory.item_category'),
      key: 'type',
      always_visible: true
    },
    {
      label: trans('road-maintenance/inventory/product.quantity'),
      key: 'quantity',
      always_visible: true
    },
    {
      label: trans('road-maintenance/prp.active_status'),
      key: 'is_active',
      always_visible: true,
      component: activeIconComponent,
      style: {
        textAlign: 'center'
      },
      src: (event) => {
        const { deactivated_at: deactivatedAt } = event?.rowData || {}
        return deactivatedAt ? '/img/icons/grey-check-circle.svg' : '/img/icons/green-check-circle.svg'
      }
    }
  ]))

  const cellsConfig = computed(() => ({
    is_active: {
      style: {
        justifyItems: 'center'
      }
    }
  }))

  const onProductCreate = () => {
    isCreating.value = true
  }

  const resetCreating = () => {
    isCreating.value = false
  }

  const config = computed(() => ({
    tableConfig: {
      key: TABLE_KEY,
      uiView: UI_VIEW,
      apiConfig,
      showLoader: isLoading.value,
      filters_and_columns_visibility_config: {
        newBtnPosition: 'start',
        newClick: onProductCreate
      },
      rowsSelection: {
        showCheckboxes: showTableCheckboxes.value,
        rememberSelectionByDataProperty: 'id',
        selectionsChange: showMarkersFn
      },
      rowDblClick: onShowProductDetails,
      // A click on a row shows it on the map beside the table
      rowClickIndicator: 'map',
      onRefreshData: fetchMarkersFn,
      sideTableComponent: {
        component: productsMapComponent,
        mapInitialized: onMapLoadFn
      },
      tableConfig: {
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value,
        paginationConfig
      }
    },
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    setLoader,
    resetCreating,
    isCreating,
    refreshData
  }
}
